import React from 'react';
import {
  Input, Select, Switch, Divider, Typography, Row, Col, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DropZone, { FileType, MyFile } from '../../../components/DropZone';
import HeaderLabel from '../../../components/Form/HeaderLabel';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import TextEditor from '../../../components/TextEditor/TextEditor';
import AlumlyButton from '../../../components/AlumlyButton';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';

const { Text } = Typography;
const { TextArea } = Input;

// Form stores datetime as a `YYYY-MM-DDTHH:mm` string (same value the API
// expects and that edit.tsx populates via `slice(0, 16)`). Convert to/from
// dayjs only for the antd DatePicker UI.
const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm';

export interface EventFormData {
  titleTh: string;
  titleEn: string;
  descriptionTh: string;
  descriptionEn: string;
  locationTh: string;
  locationEn: string;
  locationUrl: string;
  category: string;
  organizerTh: string;
  organizerEn: string;
  registrationUrl: string;
  contactTh: string;
  contactEn: string;
  formatTh: string;
  formatEn: string;
  tags: string;
  maxParticipants: string;
  startAt: string;
  endAt: string;
  isPublished: boolean;
  coverImages: MyFile[];
  galleryImages: MyFile[];
}

interface FormEventProps {
  mode: 'create' | 'edit';
  eventId?: string;
  defaultValues?: EventFormData;
}

const categories = [
  { value: 'seminar', label: 'Seminar' },
  { value: 'social', label: 'Social' },
  { value: 'sport', label: 'Sport' },
  { value: 'charity', label: 'Charity' },
];

const defaultFormValues: EventFormData = {
  titleTh: '',
  titleEn: '',
  descriptionTh: '',
  descriptionEn: '',
  locationTh: '',
  locationEn: '',
  locationUrl: '',
  category: '',
  organizerTh: '',
  organizerEn: '',
  startAt: '',
  endAt: '',
  registrationUrl: '',
  contactTh: '',
  contactEn: '',
  formatTh: '',
  formatEn: '',
  tags: '',
  maxParticipants: '',
  isPublished: false,
  galleryImages: [],
  coverImages: [],
};

const FormEvent: React.FC<FormEventProps> = ({ mode, eventId, defaultValues }) => {
  const navigate = useNavigate();
  const { service: createService } = useServices(ServiceNameEnum.event.create);
  const { service: editService } = useServices(ServiceNameEnum.event.edit);

  const { control, handleSubmit, formState: { errors } } = useForm<EventFormData>({
    defaultValues: defaultValues || defaultFormValues,
  });

  const onSubmit = async (formData: EventFormData): Promise<void> => {
    const coverFile = formData.coverImages?.[0];
    const payload = {
      titleML: { th: formData.titleTh, en: formData.titleEn },
      descriptionML: { th: formData.descriptionTh, en: formData.descriptionEn },
      locationML: { th: formData.locationTh, en: formData.locationEn },
      locationUrl: formData.locationUrl || null,
      category: formData.category || null,
      organizerML: { th: formData.organizerTh, en: formData.organizerEn },
      registrationUrl: formData.registrationUrl || null,
      contactML: { th: formData.contactTh, en: formData.contactEn },
      formatML: { th: formData.formatTh, en: formData.formatEn },
      tags: formData.tags ? formData.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
      maxParticipants: formData.maxParticipants ? Number(formData.maxParticipants) : null,
      startAt: formData.startAt || null,
      endAt: formData.endAt || null,
      isPublished: formData.isPublished,
      coverFileId: coverFile?.id || null,
      galleryFileIds: formData.galleryImages?.filter((f: MyFile) => f.id).map((f: MyFile) => f.id) || [],
    };

    if (mode === 'create') {
      const { data, error } = await createService.post(payload);
      if (data) {
        toast.success('Event created successfully');
        navigate(RouteNameEnum.event.index);
      } else if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message, { toastId: 'allToast' });
      }
    } else {
      const params = { id: eventId };
      const { data, error } = await editService.put(payload, { params });
      if (data) {
        toast.success('Event updated successfully');
        navigate(RouteNameEnum.event.index);
      } else if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message, { toastId: 'allToast' });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 32 }}>
      {/* Title */}
      <HeaderLabel label="Title" />
      <div style={{ background: 'white', padding: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Controller
              name="titleTh"
              control={control}
              rules={{ required: 'Title (TH) is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    size="large"
                    placeholder="Title (TH)"
                    status={fieldState.error ? 'error' : ''}
                  />
                  {errors.titleTh?.message && <ErrorMessage>{errors.titleTh.message}</ErrorMessage>}
                </>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <Controller
              name="titleEn"
              control={control}
              rules={{ required: 'Title (EN) is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    size="large"
                    placeholder="Title (EN)"
                    status={fieldState.error ? 'error' : ''}
                  />
                  {errors.titleEn?.message && <ErrorMessage>{errors.titleEn.message}</ErrorMessage>}
                </>
              )}
            />
          </Col>
        </Row>
      </div>

      {/* Cover Image */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Cover Image" />
        <div style={{ padding: '1rem', background: 'white' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', marginTop: 8, flexDirection: 'column',
          }}
          >
            <div style={{ display: 'flex' }}>
              <Controller
                name="coverImages"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DropZone
                    width={320}
                    height={180}
                    maxFile={1}
                    maxFileSize={1024 * 1024 * 2}
                    onChange={onChange}
                    defaultValue={value}
                    allowFileTypes={[FileType.JPEG, FileType.PNG]}
                    allowDimension={{ width: 1920, height: 1080 }}
                    err={false}
                  />
                )}
              />
            </div>
            <div>
              <ul>
                <li><Text type="secondary">1 cover image</Text></li>
                <li><Text type="secondary">Required resolution W 1920 x H 1080 px (16:9)</Text></li>
                <li><Text type="secondary">Maximum upload file size is 2 MB</Text></li>
                <li><Text type="secondary">Supported PNG, JPG, JPEG</Text></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Images */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Gallery Images" />
        <div style={{ padding: '1rem', background: 'white' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', marginTop: 8, flexDirection: 'column',
          }}
          >
            <div style={{ display: 'flex' }}>
              <Controller
                name="galleryImages"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DropZone
                    width={213}
                    height={120}
                    maxFile={8}
                    maxFileSize={1024 * 1024 * 2}
                    onChange={onChange}
                    defaultValue={value}
                    allowFileTypes={[FileType.JPEG, FileType.PNG]}
                    allowDimension={{ width: 1920, height: 1080 }}
                    err={false}
                  />
                )}
              />
            </div>
            <div>
              <ul>
                <li><Text type="secondary">Up to 8 images</Text></li>
                <li><Text type="secondary">Required resolution W 1920 x H 1080 px (16:9)</Text></li>
                <li><Text type="secondary">Maximum 2 MB per file</Text></li>
                <li><Text type="secondary">Supported PNG, JPG, JPEG</Text></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Description" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8 }}>
                <Text>Description (TH)</Text>
              </div>
              <Controller
                name="descriptionTh"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextEditor value={value} onChange={onChange} />
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8 }}>
                <Text>Description (EN)</Text>
              </div>
              <Controller
                name="descriptionEn"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextEditor value={value} onChange={onChange} />
                )}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* Location */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Location" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Controller
                name="locationTh"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="Location (TH)" />
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="locationEn"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="Location (EN)" />
                )}
              />
            </Col>
            <Col xs={24} md={24}>
              <Controller
                name="locationUrl"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="https://maps.google.com/..." />
                )}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* Details */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Details" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Controller
                name="category"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    size="large"
                    style={{ width: '100%' }}
                    placeholder="Category"
                    value={value}
                    onChange={(val) => onChange(val)}
                    options={[
                      { value: '', label: '- None -' },
                      ...categories.map((c) => ({ value: c.value, label: c.label })),
                    ]}
                  />
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="organizerTh"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="Organizer (TH)" />
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="organizerEn"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="Organizer (EN)" />
                )}
              />
            </Col>
            <Col xs={24} md={6}>
              <Controller
                name="formatTh"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="Onsite / Online / Hybrid" />
                )}
              />
            </Col>
            <Col xs={24} md={6}>
              <Controller
                name="formatEn"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="Onsite / Online / Hybrid" />
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="research, international, seminar (comma separated)" />
                )}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* Registration & Contact */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Registration & Contact" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Controller
                name="registrationUrl"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" placeholder="https://..." />
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="maxParticipants"
                control={control}
                rules={{
                  validate: (value: string) => {
                    if (!value) return true;
                    const num = Number(value);
                    if (!Number.isInteger(num) || num < 1) return 'Must be a positive integer';
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      placeholder="Unlimited (leave empty)"
                      min={1}
                      status={fieldState.error ? 'error' : ''}
                    />
                    <ErrorMessage>
                      {errors.maxParticipants?.message || 'Leave empty for unlimited'}
                    </ErrorMessage>
                  </>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="contactTh"
                control={control}
                render={({ field }) => (
                  <TextArea {...field} rows={3} placeholder="ชื่อ, เบอร์โทร, อีเมล" />
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="contactEn"
                control={control}
                render={({ field }) => (
                  <TextArea {...field} rows={3} placeholder="Name, Phone, Email" />
                )}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* Schedule */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Schedule" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Controller
                name="startAt"
                control={control}
                rules={{ required: 'Start date is required' }}
                render={({ field: { onChange, value }, fieldState }) => (
                  <>
                    <DatePicker
                      showTime
                      size="large"
                      style={{ width: '100%' }}
                      placeholder="Start Date & Time"
                      format={DATETIME_FORMAT}
                      status={fieldState.error ? 'error' : ''}
                      value={value ? dayjs(value) : null}
                      onChange={(d) => onChange(d ? d.format(DATETIME_FORMAT) : '')}
                    />
                    {errors.startAt?.message && <ErrorMessage>{errors.startAt.message}</ErrorMessage>}
                  </>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="endAt"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    showTime
                    size="large"
                    style={{ width: '100%' }}
                    placeholder="End Date & Time"
                    format={DATETIME_FORMAT}
                    value={value ? dayjs(value) : null}
                    onChange={(d) => onChange(d ? d.format(DATETIME_FORMAT) : '')}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* Visibility */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Visibility" />
        <div style={{ background: 'white', padding: 16 }}>
          <Controller
            name="isPublished"
            control={control}
            render={({ field }) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Switch checked={field.value} onChange={field.onChange} />
                <Text>Published (visible in app)</Text>
              </span>
            )}
          />
        </div>
      </div>

      {/* Actions */}
      <Divider style={{ margin: '32px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
        <AlumlyButton label="rxvWhite" width={128} onClick={() => navigate(RouteNameEnum.event.index)}>
          Cancel
        </AlumlyButton>
        <AlumlyButton label="rxv" width={200} type="submit">
          {mode === 'create' ? 'Create Event' : 'Save Changes'}
        </AlumlyButton>
      </div>
    </form>
  );
};

FormEvent.defaultProps = {
  eventId: undefined,
  defaultValues: undefined,
};

export default FormEvent;
